/*
 * Settings: device tokens for the trackside agent.
 *
 * The raw token is shown exactly once, because only its SHA-256 is stored - a
 * database dump must not hand out the right to upload the whole history. That
 * also means it genuinely cannot be recovered, so the screen has to be honest
 * about it at the moment it matters rather than in help text nobody reads.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, KeyRound, Plus, ShieldOff } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import type { User } from '../lib/api'
import { Empty, Spinner } from '../components/bits'
import { People } from '../components/People'
import { dateTime } from '../lib/format'

interface DeviceToken {
  id: number
  name: string
  revoked: boolean
  created_at: string | null
  last_seen_at: string | null
}

export function Settings({ user }: { user: User }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const tokens = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api.get<{ tokens: DeviceToken[] }>('/api/auth/tokens'),
    enabled: user.is_owner,
  })

  const create = useMutation({
    mutationFn: () => api.post<DeviceToken & { token: string }>(
      '/api/auth/tokens', { name }),
    onSuccess: (t) => {
      setIssued({ name: t.name, token: t.token })
      setName('')
      setError(null)
      qc.invalidateQueries({ queryKey: ['tokens'] })
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not issue a token.'),
  })

  const revoke = useMutation({
    mutationFn: (id: number) => api.del(`/api/auth/tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })

  if (!user.is_owner) {
    return (
      <div className="page">
        <div className="page-head"><div><h1>Settings</h1></div></div>
        <Empty title="Owner access required"
          body="Device tokens are upload credentials for the whole history, so only the owner can see or issue them." />
      </div>
    )
  }

  if (tokens.isLoading) return <div className="page"><Spinner /></div>
  const rows = tokens.data?.tokens ?? []

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">People, access, and the trackside sync agent</div>
        </div>
      </div>

      <People />

      {issued && (
        <div className="card" style={{ marginBottom: 14,
          borderColor: 'rgba(245,158,11,.45)' }}>
          <div className="card-head">
            <h2 className="row" style={{ gap: 8 }}>
              <KeyRound size={18} /> Token for “{issued.name}”
            </h2>
          </div>
          <p className="small" style={{ color: 'var(--amber)', marginTop: 0 }}>
            Copy this now. Only a hash of it is stored, so it cannot be shown
            again — if it is lost, issue a new one.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <input readOnly value={issued.token}
              style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              onFocus={(e) => e.currentTarget.select()} />
            <button className="btn ghost" onClick={() => {
              navigator.clipboard?.writeText(issued.token)
              setCopied(true)
            }}>
              <Copy size={15} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
            Put it in <code>agent.json</code> on the mechanic's laptop as
            {' '}<code>token</code>, or set <code>KART_TOKEN</code>. Then
            {' '}<code>python kartlab_agent.py --status</code> to check it.
          </p>
          <button className="btn ghost small" style={{ marginTop: 12 }}
            onClick={() => { setIssued(null); setCopied(false) }}>
            I have copied it
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><h2>Issue a token</h2></div>
        <div className="row wrap" style={{ gap: 8 }}>
          <input value={name} placeholder="mechanic laptop"
            style={{ maxWidth: 280 }}
            onChange={(e) => setName(e.target.value)} />
          <button className="btn" disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}>
            <Plus size={16} /> {create.isPending ? 'Issuing…' : 'Issue token'}
          </button>
        </div>
        {error && <div className="advice warn">{error}</div>}
        <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
          One per laptop, named so it can be revoked without guessing which is
          which.
        </p>
      </div>

      <div className="card">
        <div className="card-head"><h2>Devices</h2></div>
        {rows.length === 0 ? (
          <p className="small dim" style={{ margin: 0 }}>
            No tokens yet. The agent cannot upload anything until it has one.
          </p>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Device</th><th>Issued</th><th>Last seen</th>
                  <th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="muted small">{dateTime(t.created_at)}</td>
                    <td className="muted small">
                      {t.last_seen_at ? dateTime(t.last_seen_at)
                        : <span className="dim">never</span>}
                    </td>
                    <td>
                      <span className={`badge ${t.revoked ? 'none' : 'saved'}`}>
                        {t.revoked ? 'revoked' : 'active'}
                      </span>
                    </td>
                    <td className="num">
                      {!t.revoked && (
                        <button className="btn ghost small"
                          onClick={() => revoke.mutate(t.id)}>
                          <ShieldOff size={13} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
          Revoked rather than deleted: the row is the record that a laptop once
          had access, and when it last used it.
        </p>
      </div>
    </div>
  )
}
